/**
 * @xyflow/react wrapper for KG radial (spiral) layout.
 *
 * Accepts the same KG data as the NVL wrapper and provides matching
 * interactions: click (select), shift+click (multi-select), double-click (expand).
 */

import { useCallback, useMemo, useEffect } from 'react'
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type NodeMouseHandler,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { KgBrowserNode, KgBrowserRelationship } from '@/lib/api'
import { KgFlowNode } from './KgFlowNode'
import { KgFlowEdge } from './KgFlowEdge'
import { computeSpiralLayout } from './useSpiralLayout'

const nodeTypes = { kgNode: KgFlowNode }
const edgeTypes = { kgEdge: KgFlowEdge }

interface KgXyflowViewProps {
  kgNodes: KgBrowserNode[]
  kgRels: KgBrowserRelationship[]
  selectedNodeIds: Set<string>
  onNodeClick: (nodeId: string, shiftKey: boolean) => void
  onNodeDoubleClick: (nodeId: string) => void
  onCanvasClick: () => void
}

function KgXyflowInner({
  kgNodes,
  kgRels,
  selectedNodeIds,
  onNodeClick,
  onNodeDoubleClick,
  onCanvasClick,
}: KgXyflowViewProps) {
  const { fitView } = useReactFlow()

  const { nodes: layoutNodes, edges: layoutEdges } = useMemo(
    () => computeSpiralLayout(kgNodes, kgRels, { groupByLabel: true, selectedNodeIds }),
    [kgNodes, kgRels, selectedNodeIds],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutEdges)

  // Sync when layout data changes
  useEffect(() => {
    setNodes(layoutNodes)
    setEdges(layoutEdges)
  }, [layoutNodes, layoutEdges, setNodes, setEdges])

  // Fit view when node count changes
  useEffect(() => {
    const timer = setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50)
    return () => clearTimeout(timer)
  }, [kgNodes.length, fitView])

  const handleNodeClick: NodeMouseHandler = useCallback(
    (event, node) => {
      onNodeClick(node.id, event.shiftKey || event.metaKey)
    },
    [onNodeClick],
  )

  const handleNodeDoubleClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      onNodeDoubleClick(node.id)
    },
    [onNodeDoubleClick],
  )

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={handleNodeClick}
      onNodeDoubleClick={handleNodeDoubleClick}
      onPaneClick={onCanvasClick}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      minZoom={0.1}
      maxZoom={3}
      className="bg-white dark:bg-gray-950"
      proOptions={{ hideAttribution: true }}
    >
      <Controls className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !shadow-sm" />
      <MiniMap
        className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700"
        nodeColor="#6B7280"
        maskColor="rgba(0,0,0,0.1)"
      />
      <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#D1D5DB" />
    </ReactFlow>
  )
}

export function KgXyflowView(props: KgXyflowViewProps) {
  return (
    <ReactFlowProvider>
      <KgXyflowInner {...props} />
    </ReactFlowProvider>
  )
}
